import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { storage } from '@/utils/storage';

type Theme = 'auto' | 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  effectiveTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = '@biavent_theme';

/**
 * Obtiene el tema efectivo basado en la preferencia y el sistema
 */
function getEffectiveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'auto') {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light'; // Fallback si no hay soporte
  }
  return theme;
}

/**
 * Aplica el tema al documento HTML
 */
function applyThemeToDocument(effectiveTheme: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  
  const html = document.documentElement;
  html.classList.remove('theme-light', 'theme-dark');
  html.classList.add(`theme-${effectiveTheme}`);
  html.setAttribute('data-theme', effectiveTheme);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('auto');
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() => {
    // Inicializar con el tema del sistema o localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'auto') {
        return getEffectiveTheme(stored as Theme);
      }
    }
    return getEffectiveTheme('auto');
  });

  // Cargar tema desde localStorage al montar
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const stored = await storage.getItem(THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'auto') {
          setThemeState(stored as Theme);
          const effective = getEffectiveTheme(stored as Theme);
          setEffectiveTheme(effective);
          applyThemeToDocument(effective);
        } else {
          // Aplicar tema automático por defecto
          const effective = getEffectiveTheme('auto');
          setEffectiveTheme(effective);
          applyThemeToDocument(effective);
        }
      } catch (error) {
        console.error('Error loading theme:', error);
        const effective = getEffectiveTheme('auto');
        setEffectiveTheme(effective);
        applyThemeToDocument(effective);
      }
    };

    loadTheme();
  }, []);

  // Listener para cambios en prefers-color-scheme cuando está en 'auto'
  useEffect(() => {
    if (theme !== 'auto' || typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      const newEffective = e.matches ? 'dark' : 'light';
      setEffectiveTheme(newEffective);
      applyThemeToDocument(newEffective);
    };

    // Aplicar inmediatamente
    handleChange(mediaQuery);

    // Escuchar cambios
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback para navegadores antiguos
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [theme]);

  // Actualizar tema efectivo cuando cambia la preferencia manual
  useEffect(() => {
    if (theme !== 'auto') {
      const effective = getEffectiveTheme(theme);
      setEffectiveTheme(effective);
      applyThemeToDocument(effective);
    }
  }, [theme]);

  const setTheme = async (newTheme: Theme) => {
    try {
      setThemeState(newTheme);
      await storage.setItem(THEME_STORAGE_KEY, newTheme);
      const effective = getEffectiveTheme(newTheme);
      setEffectiveTheme(effective);
      applyThemeToDocument(effective);
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, effectiveTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
